import { useEffect } from 'react';

// Which way a key scrolls the page: 1 forward, -1 back, 0 for keys that don't.
export const scrollKeyDirection = (event: KeyboardEvent): 1 | -1 | 0 => {
  switch (event.key) {
    case 'ArrowDown':
    case 'PageDown':
    case 'End':
      return 1;
    case 'ArrowUp':
    case 'PageUp':
    case 'Home':
      return -1;
    case ' ':
      return event.shiftKey ? -1 : 1;
    default:
      return 0;
  }
};

export const isEditable = (target: EventTarget | null) => {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.isContentEditable ||
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    target.tagName === 'SELECT'
  );
};

// Holds can overlap (entrance wrapper + first-view TOC), so the html overflow is
// only restored once the last one releases.
let activeHolds = 0;
let overflowBeforeHold = '';

const acquireOverflowLock = () => {
  const root = document.documentElement;
  if (activeHolds === 0) {
    overflowBeforeHold = root.style.overflow;
    root.style.overflow = 'hidden';
  }
  activeHolds += 1;
};

const releaseOverflowLock = () => {
  activeHolds -= 1;
  if (activeHolds === 0) {
    document.documentElement.style.overflow = overflowBeforeHold;
  }
};

const LOCKED_OVERFLOW = ['hidden', 'clip'];
const isOverflowLocked = (element: Element) =>
  LOCKED_OVERFLOW.includes(getComputedStyle(element).overflowY);

// Whether a scroll lock is in effect, by computed style: an inline body lock a
// stylesheet overrides (dev-only hydration suppression) never reaches the viewport.
export const isScrollHeld = () =>
  isOverflowLocked(document.documentElement) || isOverflowLocked(document.body);

// Cancels the gestures and keys that scroll the page without touching layout.
// Imperative so it can start inside an event handler; returns the release.
export const blockScrollInput = () => {
  const blockGesture = (event: Event) => {
    if (event.cancelable) event.preventDefault();
  };
  const blockKey = (event: KeyboardEvent) => {
    if (!scrollKeyDirection(event) || isEditable(event.target)) return;
    event.preventDefault();
  };

  window.addEventListener('wheel', blockGesture, { passive: false });
  window.addEventListener('touchmove', blockGesture, { passive: false });
  window.addEventListener('keydown', blockKey);

  return () => {
    window.removeEventListener('wheel', blockGesture);
    window.removeEventListener('touchmove', blockGesture);
    window.removeEventListener('keydown', blockKey);
  };
};

// Locks overflow on <html> rather than <body>: iOS ignores a body lock for
// touch, and the dev-only hydration-error suppression forces body overflow
// with !important. Gesture and key interception backs the CSS up on iOS.
// Note that a non-scrollable document brings the mobile browser toolbar back,
// which resizes the viewport; holds that start mid-page should block input
// and pull the scroll back instead (see SectionReveal's hold).
export const holdScroll = () => {
  acquireOverflowLock();
  const releaseInput = blockScrollInput();

  return () => {
    releaseOverflowLock();
    releaseInput();
  };
};

export const useScrollHold = (active: boolean) => {
  useEffect(() => (active ? holdScroll() : undefined), [active]);
};
